import { Box, Button, ButtonProps, IconButton, Stack, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { InfoCircleOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { focusRingSx, quietActionSx } from '../../../../components/ui/styleKit';
import { RADIUS } from '../../../../components/ui/tokens';
import { pop, useTourMotion } from '../../../welcome-tour/motion';
import { useSimulateActions, useSimulateMeta } from '../SimulateTradeContext';
import { simulateOverlayZ } from '../constants';
import { SIMULATE_COPY } from '../content';
import { SimulateTradeIcon } from './SimulateTradeIcon';

const BADGE_PX = 24;
const BADGE_ICON_PX = 14;
// The disc sits concentric in the pill's rounded end: same gap above, left and below it.
const BADGE_GAP_PX = 4;
const BORDER_PX = 1;
const TRIGGER_HEIGHT_PX = BADGE_PX + 2 * (BADGE_GAP_PX + BORDER_PX);

type Props = Omit<ButtonProps, 'onClick'> & {
  /** Non-empty reason disables the trigger and is shown as its tooltip. */
  disabledReason?: string;
};

/** Solid brand disc behind the icon; it pops in once when the trigger becomes usable. */
const IconBadge = ({ active }: { active: boolean }) => {
  const theme = useTheme();
  const { v } = useTourMotion();
  const primary = theme.palette.primary.main;
  return (
    <Box
      component={motion.span}
      key={String(active)}
      variants={v(pop)}
      initial={active ? 'hidden' : false}
      animate="show"
      sx={{
        width: BADGE_PX,
        height: BADGE_PX,
        borderRadius: RADIUS.pill,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: active ? theme.palette.primary.contrastText : theme.palette.text.disabled,
        backgroundColor: active ? primary : alpha(theme.palette.text.primary, 0.08),
        boxShadow: active ? `0 6px 14px -6px ${alpha(primary, 0.9)}` : 'none'
      }}
    >
      <SimulateTradeIcon size={BADGE_ICON_PX} />
    </Box>
  );
};

export const SimulateTradeTrigger = ({ children = SIMULATE_COPY.trigger, disabled, disabledReason, sx, ...rest }: Props) => {
  const theme = useTheme();
  const { open } = useSimulateActions();
  const { masterAccountId, slaveAccountId } = useSimulateMeta();
  const accountsMissing = !masterAccountId || !slaveAccountId;
  const isDisabled = Boolean(disabled || disabledReason || accountsMissing);

  const tooltip = accountsMissing ? SIMULATE_COPY.block.accounts : disabledReason || '';

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Button
        variant="outlined"
        startIcon={<IconBadge active={!isDisabled} />}
        {...rest}
        disabled={isDisabled}
        onClick={open}
        sx={{
          ...quietActionSx(theme),
          height: TRIGGER_HEIGHT_PX,
          minHeight: TRIGGER_HEIGHT_PX,
          borderRadius: `${TRIGGER_HEIGHT_PX / 2}px`,
          borderWidth: BORDER_PX,
          pl: `${BADGE_GAP_PX}px`,
          pr: 2,
          py: 0,
          whiteSpace: 'nowrap',
          '& .MuiButton-startIcon': { ml: 0, mr: 1 },
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            borderColor: theme.palette.divider,
            backgroundColor: 'transparent'
          },
          ...(sx as object)
        }}
      >
        {children}
      </Button>
      {isDisabled && tooltip && (
        <Tooltip title={tooltip} arrow placement="top" PopperProps={{ sx: { zIndex: simulateOverlayZ } }}>
          <IconButton
            size="small"
            aria-label={tooltip}
            sx={{ ...focusRingSx(theme), p: 0.25, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            <InfoCircleOutlined style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
